// src/features/production/components/MaterialBalanceIndicator.tsx

import { MaterialBalanceResult } from "@/api/unifiedConsumption";

/**
 * AM-033 §2.5, Rule BAL-01: this component ONLY displays; it must never be
 * used to disable a submit button or block navigation. If a parent screen
 * wires `balance.is_balanced` into a `disabled` prop anywhere, that is a bug
 * relative to this AM.
 */
export function MaterialBalanceIndicator({
  balance,
  loading,
}: {
  balance: MaterialBalanceResult | null;
  loading?: boolean;
}) {
  if (loading) {
    return (
      <div
        className="brand-card-extand am033-balance am033-balance--loading"
        style={{ padding: "1.25rem", textAlign: "center", color: "var(--color-text-muted, #666)", fontWeight: 600 }}
      >
        ⏳ در حال محاسبه بالانس مواد…
      </div>
    );
  }
  if (!balance) return null;

  const metrics = [
    { label: "ورودی به ایستگاه", value: balance.issued_quantity, color: "#0284C7", bg: "rgba(2, 132, 199, 0.08)" },
    { label: "مصرف‌شده", value: balance.consumed_quantity, color: "#007270", bg: "rgba(0, 114, 112, 0.08)" },
    { label: "ضایعات", value: balance.scrapped_quantity, color: "#DC2626", bg: "rgba(220, 38, 38, 0.08)" },
    { label: "بازگشتی به انبار", value: balance.returned_quantity, color: "#D97706", bg: "rgba(217, 119, 6, 0.08)" },
    { label: "باقیمانده در ایستگاه", value: balance.remaining_at_station, color: "#4F46E5", bg: "rgba(79, 70, 229, 0.08)" },
  ];

  return (
    <div
      className={`brand-card-extand am033-balance ${
        balance.is_balanced ? "am033-balance--ok" : "am033-balance--warning"
      }`}
      role="status"
      data-testid="material-balance-indicator"
      style={{
        padding: "1.25rem 1.5rem",
        display: "flex",
        flexDirection: "column",
        gap: "1rem",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "0.5rem" }}>
        <h4 style={{ margin: 0, fontSize: "1rem", fontWeight: 800, color: "#2B2B2B", display: "flex", alignItems: "center", gap: "6px" }}>
          <span>⚖️</span>
          <span>وضعیت بالانس مواد سفارش</span>
        </h4>
        <span
          style={{
            padding: "4px 12px",
            borderRadius: "8px",
            fontSize: "0.8rem",
            fontWeight: 700,
            background: balance.is_balanced ? "rgba(16, 185, 129, 0.15)" : "rgba(245, 158, 11, 0.15)",
            color: balance.is_balanced ? "#10B981" : "#B45309",
          }}
        >
          {balance.is_balanced ? "✓ کاملاً تراز و متوازن" : "⚠️ دارای عدم تراز"}
        </span>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: "0.75rem" }}>
        {metrics.map((m) => (
          <div
            key={m.label}
            className="am033-balance__row"
            style={{
              padding: "0.75rem 1rem",
              borderRadius: "10px",
              background: m.bg,
              border: `1px solid ${m.color}25`,
              display: "flex",
              flexDirection: "column",
              gap: "4px",
            }}
          >
            <span style={{ fontSize: "0.75rem", color: "var(--color-text-muted, #666)", fontWeight: 600 }}>{m.label}</span>
            <span style={{ fontSize: "1.15rem", fontWeight: 800, color: m.color }}>{m.value}</span>
          </div>
        ))}
      </div>

      {!balance.is_balanced && (
        <div
          className="am033-balance__warning-text"
          data-testid="imbalance-warning"
          style={{
            padding: "0.75rem 1rem",
            borderRadius: "10px",
            background: "rgba(245, 158, 11, 0.1)",
            border: "1px solid #F59E0B",
            color: "#92400E",
            fontSize: "0.85rem",
            fontWeight: 600,
          }}
        >
          ⚠️ عدم بالانس: {balance.imbalance_quantity} — ثبت مسدود نشده است؛ این صرفاً یک هشدار گزارشی است.
        </div>
      )}
    </div>
  );
}
